import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Tooltip } from '@/components/ui/Tooltip'
import { Info } from 'lucide-react'
import { formatDuration, formatInteger } from './formatters'
import { slaTier, tierText } from './slaHealth'
import { cn } from '@/lib/utils'
import type { AgentPerformancePanel } from '@/types/analytics'

interface AgentPerformanceTableProps {
  data: AgentPerformancePanel
}

export function AgentPerformanceTable({ data }: AgentPerformanceTableProps) {
  return (
    <Card className="p-4">
      <CardHeader className="px-0 pb-3 flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            Agent performance
            <Tooltip content="Agent is inferred from the first reply on each conversation — we don't yet store an explicit assignee.">
              <Info className="size-3.5 text-muted-foreground cursor-help" />
            </Tooltip>
          </CardTitle>
          <CardDescription>{data.rows.length} active agents in this range</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="px-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b">
              <th className="py-2 pr-3 font-medium">Agent</th>
              <th className="py-2 px-3 font-medium text-right">Conversations</th>
              <th className="py-2 px-3 font-medium text-right">First reply</th>
              <th className="py-2 px-3 font-medium text-right">Resolve</th>
              <th className="py-2 px-3 font-medium text-right">Resolved</th>
              <th className="py-2 px-3 font-medium text-right">CSAT</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No agent activity in this range.</td></tr>
            )}
            {data.rows.map((row) => (
              <tr key={row.user_id} className="border-b last:border-b-0">
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      'inline-block w-2 h-2 rounded-full',
                      row.online_status === 'online' ? 'bg-emerald-500'
                        : row.online_status === 'away' ? 'bg-amber-500'
                        : 'bg-muted-foreground/40',
                    )} />
                    <div>
                      <div className="font-medium">{row.name}</div>
                      {row.email && <div className="text-xs text-muted-foreground">{row.email}</div>}
                    </div>
                  </div>
                </td>
                <td className="py-2 px-3 text-right tabular-nums">{formatInteger(row.conversations)}</td>
                <td className={cn('py-2 px-3 text-right tabular-nums font-medium', tierText[slaTier(row.median_first_reply_seconds, data.sla_seconds)])}>
                  {formatDuration(row.median_first_reply_seconds)}
                </td>
                <td className="py-2 px-3 text-right tabular-nums">{formatDuration(row.median_resolution_seconds)}</td>
                <td className="py-2 px-3 text-right tabular-nums">{formatInteger(row.resolved)}</td>
                <td className="py-2 px-3 text-right tabular-nums">
                  {row.csat != null ? (
                    <span>
                      {row.csat.toFixed(1)} ★
                      <span className="text-muted-foreground ml-1">({row.csat_count})</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}
